/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';

/**
 * "We couldn't load this" — the visible half of the Wave-1 failure seam.
 *
 * The SPA's recurring defect (code-health survey `03-silent-failure.md` #15)
 * was `.catch(() => ({ data: { data: [] } }))`: a rejected request rendered
 * pixel-identical to an empty account. `load()` / `Promise.allSettled` gives a
 * caller the failure flag; this is what the caller renders with it, so that a
 * failed region reads as failed and the empty state stays reserved for a
 * fulfilled response that genuinely returned zero rows.
 *
 * Placement is deliberately local: the card goes in the region that failed,
 * not across the whole page, so a screen whose other requests succeeded keeps
 * showing the data it does have. `Certificates.tsx` established that shape;
 * this component is that block extracted so every screen says it the same way.
 */
export interface LoadErrorCardProps {
  /** What failed to load, from the reader's point of view. */
  title: string;
  /**
   * Optional second line. Defaults to the reassurance that matters most: the
   * numbers on screen are missing, not zero.
   */
  hint?: string | null;
  /** Re-runs the load. Omitted only where the caller has no re-fetch to offer. */
  onRetry?: () => void;
  /** `inline` is a single row for a narrow slot (a stat row, a sidebar). */
  variant?: 'card' | 'inline';
  className?: string;
}

const DEFAULT_HINT = 'This is a loading problem, not a change to your data.';

export default function LoadErrorCard({
  title,
  hint,
  onRetry,
  variant = 'card',
  className,
}: LoadErrorCardProps) {
  const resolvedHint = hint === null ? null : (hint ?? DEFAULT_HINT);

  if (variant === 'inline') {
    return (
      <div
        role="alert"
        className={clsx(
          'card flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm',
          className
        )}
      >
        <AlertCircle size={16} className="text-accent-amber shrink-0" />
        <span className="text-text-secondary">{title}</span>
        {resolvedHint && <span className="text-xs text-text-muted">{resolvedHint}</span>}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-auto text-xs font-medium text-primary hover:underline"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  return (
    <div role="alert" className={clsx('card p-8 text-center', className)}>
      <AlertCircle size={28} className="text-accent-amber mx-auto mb-2" />
      <p className="text-sm text-text-secondary">{title}</p>
      {resolvedHint && <p className="text-xs text-text-muted mt-1">{resolvedHint}</p>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 text-xs font-medium text-primary hover:underline"
        >
          Try again
        </button>
      )}
    </div>
  );
}
